
import { useState, useCallback } from 'react';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
  duration?: number;
}

interface ToastInput {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
  duration?: number;
}

// Simple toast state management
// In a real app, this would integrate with a toast UI component
const toastListeners: ((toasts: Toast[]) => void)[] = [];
let toasts: Toast[] = [];

function updateToasts(newToasts: Toast[]) {
  toasts = newToasts;
  toastListeners.forEach(listener => listener(toasts));
}

export function useToast() {
  const [, setForceUpdate] = useState({});

  const toast = useCallback((input: ToastInput) => {
    const id = Math.random().toString(36).substring(7);
    const newToast: Toast = {
      id,
      ...input,
      duration: input.duration ?? 5000,
    };

    updateToasts([...toasts, newToast]);

    // Auto-dismiss
    if (newToast.duration) {
      setTimeout(() => {
        updateToasts(toasts.filter(t => t.id !== id));
      }, newToast.duration);
    }

    // Log for development
    const prefix = input.variant === 'destructive' ? '❌' : input.variant === 'success' ? '✅' : 'ℹ️';
    console.log(`${prefix} Toast: ${input.title || ''} ${input.description || ''}`);

    return id;
  }, []);

  const dismiss = useCallback((toastId: string) => {
    updateToasts(toasts.filter(t => t.id !== toastId));
  }, []);

  const dismissAll = useCallback(() => {
    updateToasts([]);
  }, []);

  return {
    toast,
    dismiss,
    dismissAll,
    toasts,
  };
}

export type { ToastInput };
